/**
 * Extension Detection Utility
 *
 * Detects if a user message starts with an extension name and provides
 * relevant information about that extension.
 */

import { ComposioExtension } from '@/types/extensions'

// Extension names mapping (case-insensitive)
// Updated to use simple extension names instead of UUIDs
const EXTENSION_NAMES_MAP: Record<string, string> = {
  // Gmail variations
  gmail: 'gmail',
  email: 'gmail',
  mail: 'gmail',

  // GitHub variations
  github: 'github',
  git: 'github',

  // Google Calendar variations
  calendar: 'googlecalendar',
  'google calendar': 'googlecalendar',
  gcal: 'googlecalendar',

  // Google Sheets variations
  sheets: 'googlesheets',
  'google sheets': 'googlesheets',
  gsheets: 'googlesheets',
  spreadsheet: 'googlesheets',

  // Twitter/X variations
  twitter: 'twitter',
  x: 'twitter',

  // Notion variations
  notion: 'notion',

  // Google Drive variations
  drive: 'googledrive',
  'google drive': 'googledrive',
  gdrive: 'googledrive',

  // Slack variations
  slack: 'slack',

  // Outlook variations
  outlook: 'outlook',
  'microsoft outlook': 'outlook',

  // HubSpot variations
  hubspot: 'hubspot',

  // Google Docs variations
  docs: 'googledocs',
  'google docs': 'googledocs',
  gdocs: 'googledocs',
  document: 'googledocs',

  // Jira variations
  jira: 'jira',

  // YouTube variations
  youtube: 'youtube',
  yt: 'youtube',

  // Asana variations
  asana: 'asana',

  // Google Tasks variations
  tasks: 'googletasks',
  'google tasks': 'googletasks',
  gtasks: 'googletasks',

  // ClickUp variations
  clickup: 'clickup',

  // Reddit variations
  reddit: 'reddit',

  // LinkedIn variations
  linkedin: 'linkedin',

  // Microsoft Teams variations
  teams: 'microsoftteams',
  'microsoft teams': 'microsoftteams',
  'ms teams': 'microsoftteams',

  // Wrike variations
  wrike: 'wrike',

  // Calendly variations
  calendly: 'calendly',

  // Google Photos variations
  photos: 'googlephotos',
  'google photos': 'googlephotos',
  gphotos: 'googlephotos',

  // Zoom variations
  zoom: 'zoom',

  // Google Meet variations
  meet: 'googlemeet',
  'google meet': 'googlemeet',
  gmeet: 'googlemeet',

  // OneDrive variations
  onedrive: 'onedrive',
  'microsoft onedrive': 'onedrive',

  // Dropbox variations
  dropbox: 'dropbox'
}

export interface ExtensionDetectionResult {
  detected: boolean
  extensionId?: string
  extensionName?: string
  matchedKeyword?: string
  remainingMessage?: string
  fullMessage: string
}

/**
 * Detects if a message starts with an extension name
 * @param message The user input message
 * @returns Detection result with extension information
 */
export function detectExtensionInMessage(message: string): ExtensionDetectionResult {
  const trimmedMessage = message.trim()

  if (!trimmedMessage) {
    return {
      detected: false,
      fullMessage: message
    }
  }

  // Check for various patterns at the start of the message
  const patterns = [
    // "Gmail list my emails" -> keyword: "gmail", rest: "list my emails"
    /^([a-zA-Z\s]+?)(?:\s+(.+))?$/,
    // "Gmail, list my emails" -> keyword: "gmail", rest: "list my emails"
    /^([a-zA-Z\s]+?),\s*(.+)$/,
    // "Gmail: list my emails" -> keyword: "gmail", rest: "list my emails"
    /^([a-zA-Z\s]+?):\s*(.+)$/
  ]

  for (const pattern of patterns) {
    const match = trimmedMessage.match(pattern)
    if (match) {
      const potentialKeyword = match[1]?.trim().toLowerCase()
      const remainingMessage = match[2]?.trim() || ''

      // Check if the keyword matches any extension
      if (potentialKeyword && EXTENSION_NAMES_MAP[potentialKeyword]) {
        const extensionId = EXTENSION_NAMES_MAP[potentialKeyword]

        return {
          detected: true,
          extensionId,
          extensionName: getExtensionNameById(extensionId),
          matchedKeyword: potentialKeyword,
          remainingMessage,
          fullMessage: message
        }
      }
    }
  }

  // Check for single word extension names at the start
  const words = trimmedMessage.split(/\s+/)
  const firstWord = words[0]?.toLowerCase()

  if (firstWord && EXTENSION_NAMES_MAP[firstWord]) {
    const extensionId = EXTENSION_NAMES_MAP[firstWord]
    const remainingMessage = words.slice(1).join(' ').trim()

    return {
      detected: true,
      extensionId,
      extensionName: getExtensionNameById(extensionId),
      matchedKeyword: firstWord,
      remainingMessage,
      fullMessage: message
    }
  }

  return {
    detected: false,
    fullMessage: message
  }
}

/**
 * Gets the official extension name by ID
 */
function getExtensionNameById(extensionId: string): string {
  const extensionNames: Record<string, string> = {
    gmail: 'Gmail',
    github: 'GitHub',
    googlecalendar: 'Google Calendar',
    googlesheets: 'Google Sheets',
    twitter: 'X (Twitter)',
    notion: 'Notion',
    googledrive: 'Google Drive',
    slack: 'Slack',
    outlook: 'Outlook',
    hubspot: 'HubSpot',
    googledocs: 'Google Docs',
    jira: 'Jira',
    youtube: 'YouTube',
    asana: 'Asana',
    googletasks: 'Google Tasks',
    clickup: 'ClickUp',
    reddit: 'Reddit',
    linkedin: 'LinkedIn',
    microsoftteams: 'Microsoft Teams',
    wrike: 'Wrike',
    calendly: 'Calendly',
    googlephotos: 'Google Photos',
    zoom: 'Zoom',
    googlemeet: 'Google Meet',
    onedrive: 'OneDrive',
    dropbox: 'Dropbox'
  }

  return extensionNames[extensionId] || 'Unknown Extension'
}

/**
 * Gets all supported extension keywords for autocomplete/suggestions
 */
export function getAllExtensionKeywords(): string[] {
  return Object.keys(EXTENSION_NAMES_MAP).sort()
}
